import { EventEmitter } from './helpers/EventEmitter';
import {
  XHR_UNSENT,
  XHR_DONE,
  XHR_HEADERS_RECEIVED,
} from './shared/constants/Constants';
import isObject from './shared/isObject';
import DataStore from './DataStore';
import autobind from 'class-autobind';

const dataStore = DataStore.getInstance('app');

type Headers = {
  [name: string]: string;
};

type Options = {
  headers?: Headers;
  errorEarly?: boolean;
};

export type Response = {
  status: number;
  headers: Headers;
  // TODO: Change this to ObjectOf<JSONValue>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  body?: ObjectOf<any>;
};

const DEFAULT_OPTIONS: Options = {
  // Abort upload immediately upon receiving HTTP status other than 200
  errorEarly: false,
};

type Events = {
  progress: [number, number];
  complete: [Error | null, Response];
};

export default class FileUpload extends EventEmitter<Events> {
  url: string;
  file: File;
  options: Options;
  xhr: XMLHttpRequest;
  response: Response;
  headersReceived = false;
  done = false;

  constructor(url: string, file: File, options?: Options | null | undefined) {
    super();
    autobind(this);
    this.url = url;
    this.file = file;
    this.options = Object.assign({}, DEFAULT_OPTIONS, options);
    this.xhr = new XMLHttpRequest();
    this.response = {
      status: 0,
      headers: {},
    };
  }

  send() {
    let xhr = this.xhr;
    xhr.addEventListener('readystatechange', this._onUploadStateChange);
    xhr.addEventListener('load', this._onUploadComplete);
    xhr.addEventListener('error', this._onUploadError);
    xhr.upload.addEventListener('progress', this._onUploadProgress);
    xhr.open('POST', this.url, true);
    let file = this.file;
    let headers: Headers = {
      'Content-Type': 'application/octet-stream',
      'Content-Disposition':
        'attachment; filename="' + encodeURI(file.name) + '"',
      'Content-Description': file.type,
    };
    Object.assign(headers, this.options.headers);
    let account = dataStore.get('account');
    let vendorAccount = dataStore.get('vendorAccount');
    if (vendorAccount) {
      headers['X-Vendor-Auth-Token'] = vendorAccount.token || '';
    }
    if (account) {
      headers['X-Auth-Token'] = account.token || '';
    }
    let accountID = dataStore.get('accountID');
    if (accountID) {
      headers['X-Account-ID'] = accountID;
    }
    Object.entries(headers).forEach(([name, value]) => {
      xhr.setRequestHeader(name, value);
    });
    xhr.responseType = 'text';
    xhr.send(file);
  }

  abort() {
    this._endRequest();
  }

  _saveHeaders() {
    let { xhr, response } = this;
    response.status = xhr.status === 1223 ? 204 : xhr.status;
    let { headers } = response;
    let pairs = xhr.getAllResponseHeaders().trim().split('\n');
    pairs.forEach((header) => {
      let split = header.trim().split(':');
      let name = String(split.shift()).trim();
      let value = split.join(':').trim();
      let key = name.toLowerCase();
      headers[key] = headers[key] == null ? value : headers[key] + ', ' + value;
    });
  }

  _onUploadStateChange() {
    if (this.xhr.readyState === XHR_HEADERS_RECEIVED) {
      if (this.headersReceived) {
        return;
      }
      this._saveHeaders();
      this.headersReceived = true;
      let status = this.response.status;
      if (status < 100 || status > 599) {
        this._handleUploadError(
          new Error(t('Unexpected response status {status}', { status })),
        );
      } else if (this.options.errorEarly && status !== 200) {
        this._onUploadComplete();
      }
    }
  }

  _onUploadProgress(event: ProgressEvent<XMLHttpRequestEventTarget>) {
    if (event.lengthComputable) {
      this.emit('progress', event.loaded, event.total);
    }
  }

  _onUploadComplete() {
    let { xhr, response } = this;
    let contentTypeRaw = response.headers['content-type'] || '';
    let contentType = String(contentTypeRaw.split(';')[0]).toLowerCase();
    if (this.xhr.readyState !== XHR_DONE) {
      // We aborted this request early, based on options.errorEarly and a
      // non-OK response status.
      let errorMessage =
        response.headers['x-error-message'] || `HTTP Status ${response.status}`;
      this._handleUploadError(new Error(errorMessage));
      return;
    } else if (contentType === 'application/json') {
      try {
        let parsed = JSON.parse(xhr.responseText);
        response.body = isObject(parsed) ? parsed : { _: parsed };
      } catch (e) {
        let error = new Error(
          t('Unable to parse JSON response: {errorMessage}', {
            errorMessage: e.message,
          }),
        );
        this._handleUploadError(error);
        return;
      }
    } else {
      response.body = { __BODY__: xhr.responseText };
    }
    this._endRequest();
    this.emit('complete', null, response);
  }

  _onUploadError(_event: ProgressEvent<XMLHttpRequestEventTarget>) {
    this._handleUploadError(new Error(t('Network request failed')));
  }

  _handleUploadError(error: Error) {
    this._endRequest();
    this.emit('complete', error, this.response);
  }

  _endRequest() {
    if (this.done) {
      return;
    }
    let xhr = this.xhr;
    xhr.removeEventListener('readystatechange', this._onUploadStateChange);
    xhr.removeEventListener('load', this._onUploadComplete);
    xhr.removeEventListener('error', this._onUploadError);
    xhr.upload.removeEventListener('progress', this._onUploadProgress);
    if (xhr.readyState !== XHR_UNSENT && xhr.readyState !== XHR_DONE) {
      xhr.abort();
    }
    this.done = true;
  }
}
